import React, { useId, useState } from "react";
import {
  ArrowRightLeft, CalendarClock, CheckCircle2, ChevronDown, ChevronRight, Clock, Play, RotateCcw, Square, Undo2, Video,
} from "lucide-react";
import { AppBadge, AppButton, AppCard, ConfirmModal, StatusBadge, cn } from "../ui";
import TopicRow from "./TopicRow";
import { AttachmentActions, AttachmentChips } from "./SyllabusAttachments";
import SessionDateLine from "./schedule/SessionDateLine";
import { labelOf } from "./schedule/scheduleFormat";

const formatTime = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":").map(Number);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
};

/**
 * SyllabusDayCard — one day of the trainer's syllabus, collapsible.
 *
 * Props:
 *   session          — the day (GET /syllabus/batch/:id/syllabus, staff shape)
 *   isCurrent, isExpanded, onToggleExpand
 *   classLive        — the batch's class is running, so Start only starts the day
 *   onStart, onEnd, onRestart, onReopen, onMarkCompleted
 *   onToggleTopic    — (topicIndex)
 *   onReschedule     — omit to hide
 *   onCarryOver      — omit to hide
 *   recordings on the day (`session.recordings`) and each topic:
 *   onMapRecording   — (topicIndex | null)
 *   onOpenRecording  — (recording)
 *   onRemoveRecording — (recording, topicIndex | null)
 *   materials        — { day: [], topics: { [topicIndex]: [] }, loading }
 *   onUploadMaterial — (topicIndex | null)
 *   onLinkMaterial   — (topicIndex | null)
 *   onOpenMaterial, onRemoveMaterial
 *   removingId       — the recording or material being removed right now
 *   actionLoading    — this day is saving
 *   batchTiming      — { startTime, endTime }
 */
const SyllabusDayCard = ({
  session, isCurrent, isExpanded, onToggleExpand, classLive,
  onStart, onEnd, onRestart, onReopen, onMarkCompleted,
  onToggleTopic, onReschedule, onCarryOver,
  onMapRecording, onOpenRecording, onRemoveRecording,
  materials = {}, onUploadMaterial, onLinkMaterial, onOpenMaterial, onRemoveMaterial, removingId,
  actionLoading, batchTiming,
}) => {
  const [confirmAction, setConfirmAction] = useState(null);
  const bodyId = useId();
  if (!session) return null;

  const topics = session.topics || [];
  const completedTopics = topics.filter((t) => t.status === "completed").length;
  const state = session.schedule?.state || session.status || "scheduled";
  const hasUnticked = topics.some((t) => t.status !== "completed");
  const recordingCount = (session.recordings?.length || 0)
    + topics.reduce((n, t) => n + (t.recordings?.length || 0), 0);

  const confirm = (action) => setConfirmAction(action);
  const markCompleted = () => confirm({
    title: "Mark completed",
    message: "Mark this day as completed? Every topic is ticked as covered.",
    variant: "success",
    confirmText: "Mark completed",
    onConfirm: onMarkCompleted,
  });

  return (
    <>
      <AppCard as="li" overflowVisible className={cn("tw-list-none", isCurrent && session.status !== "completed" && "tw-border-primary/60")}>
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          className="tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-4 tw-text-left tw-focus-ring"
        >
          <span className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted">
            {isExpanded ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
          </span>
          <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
            <span className="tw-text-sm tw-font-semibold tw-text-content">
              {labelOf(session)} — {session.title}
            </span>
            <SessionDateLine session={session} />
            <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-content-muted">
              {batchTiming?.startTime && batchTiming?.endTime && (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <Clock size={12} aria-hidden="true" />
                  {formatTime(batchTiming.startTime)} – {formatTime(batchTiming.endTime)}
                </span>
              )}
              {topics.length > 0 && <span>{completedTopics}/{topics.length} topics covered</span>}
              {recordingCount > 0 && (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <Video size={12} aria-hidden="true" />
                  {recordingCount === 1 ? "1 recording" : `${recordingCount} recordings`}
                </span>
              )}
            </span>
          </span>
          <span className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-1.5">
            {isCurrent && session.status !== "completed" && <AppBadge size="sm" color="primary">Current</AppBadge>}
            <StatusBadge domain="syllabusDay" status={state} />
          </span>
        </button>

        {isExpanded && (
          <div id={bodyId} className="tw-flex tw-flex-col tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-4 tw-pb-4 tw-pt-3">
            <section className="tw-flex tw-flex-col tw-gap-2" aria-label="Topics">
              <h4 className="tw-m-0 tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-muted">
                Topics
                <span className="tw-font-medium tw-normal-case tw-tracking-normal">{completedTopics}/{topics.length} covered</span>
              </h4>
              {topics.length ? (
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
                  {topics.map((topic, index) => (
                    <TopicRow
                      key={topic.key || index}
                      index={index}
                      topic={topic}
                      sessionStatus={session.status}
                      busy={actionLoading}
                      onToggle={() => onToggleTopic(index)}
                      onMapRecording={() => onMapRecording(index)}
                      onOpenRecording={onOpenRecording}
                      onRemoveRecording={(rec) => onRemoveRecording(rec, index)}
                      materials={materials.topics?.[index] || []}
                      removingId={removingId}
                      onUploadMaterial={() => onUploadMaterial(index)}
                      onLinkMaterial={() => onLinkMaterial(index)}
                      onOpenMaterial={onOpenMaterial}
                      onRemoveMaterial={onRemoveMaterial}
                    />
                  ))}
                </ul>
              ) : (
                <p className="tw-m-0 tw-text-xs tw-text-content-muted">No topics for this day.</p>
              )}
            </section>

            <section className="tw-flex tw-flex-col tw-gap-1.5" aria-label="For the whole day">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-2 tw-gap-y-1">
                <h4 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-muted">
                  For the whole day
                </h4>
                <AttachmentActions
                  target="the whole day"
                  onMapRecording={() => onMapRecording(null)}
                  onUpload={() => onUploadMaterial(null)}
                  onLink={() => onLinkMaterial(null)}
                />
              </div>
              <AttachmentChips
                where="this day"
                recordings={session.recordings || []}
                materials={materials.day || []}
                materialsLoading={materials.loading}
                removingId={removingId}
                onOpenRecording={onOpenRecording}
                onRemoveRecording={(rec) => onRemoveRecording(rec, null)}
                onOpenMaterial={onOpenMaterial}
                onRemoveMaterial={onRemoveMaterial}
              />
            </section>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-3">
              {session.status === "scheduled" && (
                <>
                  <AppButton onClick={onStart} disabled={actionLoading}>
                    {classLive ? <Play size={15} aria-hidden="true" /> : <Video size={15} aria-hidden="true" />}
                    {actionLoading ? "Starting…" : classLive ? "Start this day" : "Start class"}
                  </AppButton>
                  <AppButton variant="outline" onClick={markCompleted} disabled={actionLoading}>
                    <CheckCircle2 size={15} aria-hidden="true" /> Mark completed
                  </AppButton>
                  {onReschedule && (
                    <AppButton variant="outline" onClick={onReschedule} disabled={actionLoading}>
                      <CalendarClock size={15} aria-hidden="true" /> Reschedule
                    </AppButton>
                  )}
                </>
              )}

              {session.status === "in_progress" && (
                <>
                  <AppButton variant="success" onClick={markCompleted} disabled={actionLoading}>
                    <CheckCircle2 size={15} aria-hidden="true" /> Mark completed
                  </AppButton>
                  <AppButton variant="outline-destructive" onClick={onEnd} disabled={actionLoading}>
                    <Square size={15} aria-hidden="true" /> End day
                  </AppButton>
                  {onReschedule && (
                    <AppButton variant="outline" onClick={onReschedule} disabled={actionLoading}>
                      <CalendarClock size={15} aria-hidden="true" /> Reschedule
                    </AppButton>
                  )}
                  <AppButton
                    variant="ghost"
                    onClick={() => confirm({
                      title: "Restart day",
                      message: "Restart this day? Its topics go back to not covered. Recordings are kept.",
                      variant: "warning",
                      confirmText: "Restart",
                      onConfirm: onRestart,
                    })}
                    disabled={actionLoading}
                  >
                    <RotateCcw size={15} aria-hidden="true" /> Restart
                  </AppButton>
                </>
              )}

              {session.status === "completed" && (
                <>
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-success">
                    <CheckCircle2 size={16} aria-hidden="true" /> Day completed
                  </span>
                  {onCarryOver && hasUnticked && (
                    <AppButton variant="outline" onClick={onCarryOver} disabled={actionLoading}>
                      <ArrowRightLeft size={15} aria-hidden="true" /> Carry over unfinished topics
                    </AppButton>
                  )}
                  <AppButton
                    variant="outline"
                    onClick={() => confirm({
                      title: "Reopen day",
                      message: "Reopen this day? It goes back to in progress so you can change it.",
                      variant: "warning",
                      confirmText: "Reopen",
                      onConfirm: onReopen,
                    })}
                    disabled={actionLoading}
                  >
                    <Undo2 size={15} aria-hidden="true" /> Reopen
                  </AppButton>
                </>
              )}
            </div>
          </div>
        )}
      </AppCard>

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => { const run = confirmAction?.onConfirm; setConfirmAction(null); run?.(); }}
        title={confirmAction?.title || ""}
        message={confirmAction?.message || ""}
        confirmText={confirmAction?.confirmText || "Confirm"}
        variant={confirmAction?.variant || "danger"}
      />
    </>
  );
};

export default SyllabusDayCard;
